import { useRef } from "react";
import { ShieldCheck, Brain, Zap } from "lucide-react";
import { Reveal, Tag, H2 } from "@/components/sections/shared";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";
import CursorGrid from "@/components/MagicBento/CursorGrid";
import FoldText from "@/components/FoldText/FoldText";
import TiltedCard from "@/components/TiltedCard/TiltedCard";

const cards = [
  { icon: ShieldCheck, title: "Trustworthy", copy: "Jack explains things clearly without scare tactics or fake promises." },
  { icon: Brain, title: "Knowledgeable", copy: "Built from decades of insurance industry experience, Jack understands the language, patterns, and pressure points of the claims process." },
  { icon: Zap, title: "Empowering", copy: "Jack helps you feel less stuck and more informed before you decide what to do next." },
];

export default function MeetJack() {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();

  return (
    <section id="meet-jack" data-testid="meet-jack-section" className="relative bg-[#0A0A0C] py-24 md:py-32 overflow-hidden">
      <GlobalSpotlight
        gridRef={gridRef}
        disableAnimations={isMobile}
        glowColor="138, 43, 226"
        spotlightRadius={280}
      />
      <CursorGrid
        disableAnimations={isMobile}
        highlightColor="184, 133, 245"
        gridSize={60}
        radius={700}
        intensity={0.05}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(700px circle at 20% 50%, rgba(138,43,226,0.12), transparent 60%)" }}
      />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-14 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-4">
          <div className="relative max-w-[340px] mx-auto lg:mx-0">
            <button
              type="button"
              onClick={() => window.AIWidget?.ask()}
              aria-label="Ask Jack"
              className="block w-full cursor-pointer"
            >
              <TiltedCard
                imageSrc="/jack.png"
                altText="Jack D. Hapsburg"
                testId="meet-jack-portrait"
                containerHeight="420px"
                containerWidth="100%"
                imageHeight="420px"
                imageWidth="100%"
                scaleOnHover={1.05}
                rotateAmplitude={10}
                showMobileWarning={false}
                showTooltip={false}
                className="tilted-card-figure--square"
                innerClassName="rounded-t-full overflow-hidden border-2 border-[#FFD166]/60 shadow-[0_30px_80px_-10px_rgba(138,43,226,0.4)]"
                imgClassName="object-top"
              />
            </button>
            <div className="absolute -right-4 lg:-right-10 -bottom-6 max-w-[260px] bg-[#1A1A1D] border border-[#8A2BE2]/50 p-4 rotate-[-2deg]">
              <p className="font-hand text-xl text-[#FFD166] leading-tight">
                "Insurance companies know the rules of the game. I help you understand the game
                before you make your next move."
              </p>
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-8 lg:pl-8">
          <Reveal>
            <Tag testId="meet-jack-tag">Meet Jack</Tag>
            <H2>
              <FoldText
                text="Meet Jack D. Hapsburg."
                splitBy="word"
                trigger="scroll"
                fontSize="inherit"
                fontWeight="inherit"
                color="inherit"
              />
            </H2>
            <p className="mt-3 font-serif-jack italic text-2xl text-[#9D4EDD]">
              The insurance insider in your corner.
            </p>
            <p className="mt-6 text-[#A1A1AA] font-light leading-relaxed max-w-2xl">
              Jack is an insurance industry veteran with 30 years of insider experience,
              backed by AI-powered claim intelligence to help everyday people understand their
              insurance claims before they get overwhelmed by the system. He does not replace
              a lawyer. He does not guarantee an outcome. He does not pretend every claim is a
              lawsuit. Jack helps you understand your situation, recognize possible problems, avoid
              common mistakes, and decide what your next best step may be.
            </p>
          </Reveal>
          <div ref={gridRef} className="bento-section mt-10 grid sm:grid-cols-3 gap-5">
            {cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <ParticleCard
                  testId={`jack-trait-${i}`}
                  disableAnimations={isMobile}
                  glowColor="138, 43, 226"
                  className="magic-bento-card magic-bento-card--border-glow h-full bg-[#1A1A1D] border border-white/10 p-6 hover:border-[#8A2BE2]/60 transition-colors duration-300"
                >
                  <c.icon size={20} className="text-[#FFD166]" />
                  <h3 className="mt-3 font-display text-base tracking-wide text-white">{c.title}</h3>
                  <p className="mt-2 text-sm text-[#A1A1AA] font-light leading-relaxed">{c.copy}</p>
                </ParticleCard>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
